import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { getAuthenticatedDomains, registerAuthenticatedDomain, checkAuthenticatedDomain, deleteAuthenticatedDomain } from '../../../API/PhishingAPI';
import CopyableDeliveryValue from './CopyableDeliveryValue';


export default function AuthenticatedDomains({ t }) {
  const [rows, setRows] = useState(null), [domain, setDomain] = useState(''), [error, setError] = useState('');
  const [busy, setBusy] = useState(''), [localReview, setLocalReview] = useState(false), [remove, setRemove] = useState(null);
  const mounted = useRef(true), pending = useRef(false), generation = useRef(0);
  const load = useCallback(async () => {
    const request = ++generation.current;
    setError('');
    try {
      const result = await getAuthenticatedDomains();
      if (!Array.isArray(result?.domains)) throw new Error('Invalid domain registry');
      if (mounted.current && request === generation.current) { setRows(result.domains); setLocalReview(!!result.localReview); }
    } catch (error) { if (mounted.current && request === generation.current) setError(error.message || 'Domain registry unavailable'); }
  }, []);
  useEffect(() => { mounted.current = true; load(); return () => { mounted.current = false; ++generation.current; }; }, [load]);
  async function mutate(kind, item) {
    if (pending.current) return;
    pending.current = true; ++generation.current; setBusy(kind); setError('');
    try {
      if (kind === 'add') {
        const saved = await registerAuthenticatedDomain(domain.trim());
        if (mounted.current) { setRows(current => [...(current || []).filter(old => old.id !== saved.id), saved]); setDomain(''); }
      } else if (kind === 'remove') {
        await deleteAuthenticatedDomain(item.id);
        if (mounted.current) { setRows(current => current.filter(old => old.id !== item.id)); setRemove(null); }
      } else {
        const items = item ? [item] : rows || [];
        for (const target of items) {
          const checked = await checkAuthenticatedDomain(target.id);
          if (!mounted.current) return;
          setRows(current => current.map(old => old.id === checked.id ? checked : old));
        }
      }
    } catch (error) {
      if (mounted.current) {
        setError(error.message || 'Domain registry unavailable');
        if (kind === 'check') setRows(current => current.map(old => !item || old.id === item.id ? { ...old, verified: false, status: 'error' } : old));
      }
    } finally { pending.current = false; if (mounted.current) setBusy(''); }
  }
  return <section className="delivery-settings__section authenticated-domains" aria-labelledby="authenticated-domains-title">
    <div className="authenticated-domains__title"><h2 id="authenticated-domains-title"><span className="delivery-settings__step-number" aria-hidden="true">1</span>{t('authenticated_domains.title')}</h2>
      {rows?.length > 1 && <Button color="primary" disabled={!!busy} onClick={() => mutate('check')}>{t('authenticated_domains.check_all')}</Button>}</div>
    <p>{t('authenticated_domains.description')}</p>
    <p className="delivery-settings__note">{t('authenticated_domains.proof_help')}</p>
    {localReview && <Alert color="warning">{t('authenticated_domains.local_review')}</Alert>}
    {error && <Alert color="danger" role="alert">{t('authenticated_domains.error', { message: error })}{' '}
      <Button color="link" disabled={!!busy} onClick={load}>{t('template_gallery.retry')}</Button></Alert>}
    <form className="authenticated-domains__add" onSubmit={event => { event.preventDefault(); if (domain.trim()) mutate('add'); }}>
      <div><Label for="authenticated-domain-name">{t('authenticated_domains.domain')}</Label>
        <Input id="authenticated-domain-name" value={domain} disabled={!!busy || rows === null} placeholder="empresa.com.br" onChange={event => setDomain(event.target.value)} /></div>
      <Button type="submit" color="primary" disabled={!!busy || !domain.trim() || rows === null}>{t('authenticated_domains.add')}</Button>
    </form>
    {rows === null && !error && <p role="status"><Spinner size="sm" tag="span" /> {t('simulation_wizard.loading')}</p>}
    {rows?.length === 0 && <p>{t('authenticated_domains.empty')}</p>}
    {rows?.map(item => <article className={`authenticated-domains__card${!item.verified && item.status === 'missing' ? ' authenticated-domains__card--dns-missing' : ''}`} key={item.id}>
      <div className="authenticated-domains__heading"><h3>{item.domain}</h3>
        <span role="status" className={`delivery-settings__status${item.verified ? ' delivery-settings__status--published' : item.status === 'missing' ? ' authenticated-domains__status--missing' : ''}`}>
          {!item.verified && item.status === 'missing' && <span className="authenticated-domains__status-icon" aria-hidden="true">!</span>}
          {t(`authenticated_domains.status_${item.status || 'pending'}`)}</span>
        <div><Button color="link" size="sm" disabled={!!busy} onClick={() => setRemove(item)} aria-label={t('authenticated_domains.remove_domain', { domain: item.domain })}>{t('authenticated_domains.remove')}</Button></div></div>
      <div className="authenticated-domains__records">
        <CopyableDeliveryValue label={t('authenticated_domains.txt_host')} value={item.dns_host} t={t} />
        <CopyableDeliveryValue label={t('authenticated_domains.txt_value')} value={item.dns_value} t={t} />
      </div>
      <div className="authenticated-domains__next-step">
        <Button color="primary" size="sm" disabled={!!busy} onClick={() => mutate('check', item)} aria-label={t('authenticated_domains.check_domain', { domain: item.domain })}>{busy === 'check' ? <Spinner size="sm" tag="span" className="mr-2" /> : null}{t('delivery_settings.check')}</Button>
        <p>{t(item.verified ? 'authenticated_domains.verified_next' : 'authenticated_domains.publish_help')}</p>
        {!item.verified && item.checked_at && <p className="delivery-settings__note">{t('authenticated_domains.retry_help')}</p>}
      </div>
      {item.checked_at && <p className="delivery-settings__note">{t('delivery_settings.checked_at', { date: new Date(item.checked_at).toLocaleString() })}</p>}
    </article>)}
    {remove && <Modal isOpen toggle={() => { if (!busy) setRemove(null); }}>
      <ModalHeader toggle={busy ? undefined : () => setRemove(null)}>{t('authenticated_domains.remove')}</ModalHeader>
      <ModalBody>{t('authenticated_domains.remove_help', { domain: remove.domain })}</ModalBody>
      <ModalFooter><Button color="link" disabled={!!busy} onClick={() => setRemove(null)}>{t('actions.cancel')}</Button>
        <Button color="danger" disabled={!!busy} onClick={() => mutate('remove', remove)}>{t('authenticated_domains.remove')}</Button></ModalFooter>
    </Modal>}
  </section>;
}
